import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { callAPI } from "../../../lib/common";
import Button from "@mui/material/Button";
import Popup, {
  PopupContent,
  PopupHeading,
} from "../../../components/Common/Popup";
import Box from "@mui/material/Box";
import LoadingButton from "../../../components/LoadingButton";
import makeStyles from "@mui/styles/makeStyles";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import FormikTextField from "../../../components/Common/FormikTextField";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const useStyles = makeStyles((theme) => ({
  codeTextField: {
    marginRight: theme.spacing(2),
    flexBasis: 1,
    flexGrow: 1,
    margin: 0,
  },
}));

const ForgotPasswordDialog = ({ email, onClose }) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [completeMessage, setCompleteMessage] = useState("");
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Popup open={true} onClose={onClose}>
      <PopupHeading>
        <Trans>Forgot your password?</Trans>
      </PopupHeading>

      <PopupContent>
        <Formik
          initialValues={{ emailAddress: email }}
          validateOnMount={true}
          validationSchema={formSchema(t)}
          onSubmit={(values) => {
            setSending(true);
            setError("");
            const email = encodeURIComponent(values.emailAddress);
            callAPI(
              `/sections/prophoto/ajax/send_password_reminder.php?json=1&email=${email}`,
            )
              .then((res) => {
                if (res.error) {
                  setError(res.error);
                } else {
                  setCompleteMessage(res.message);
                }
              })
              .catch(() => {
                setError(t("Error sending password reset instructions."));
              })
              .finally(() => {
                setSending(false);
              });
          }}
        >
          {({ isValid }) => {
            return (
              <Form className={classes.form}>
                {error && (
                  <p style={{ color: "red", textAlign: "center" }}>{error}</p>
                )}
                {!completeMessage && (
                  <>
                    <p>
                      <Trans>
                        Enter your e-mail address below, and an email with a
                        link to reset your password will be sent immediately.
                      </Trans>
                    </p>
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Box
                        maxWidth={500}
                        display="flex"
                        flexDirection={isXs ? "column" : "row"}
                      >
                        <Field
                          component={FormikTextField}
                          label={t("Email address")}
                          variant="outlined"
                          className={classes.codeTextField}
                          type="email"
                          name="emailAddress"
                        />
                        <Box
                          mt={2}
                          ml={isXs ? 0 : 2}
                          display="flex"
                          justifyContent={isXs ? "flex-end" : "initial"}
                          width={isXs ? "100%" : "auto"}
                        >
                          <LoadingButton
                            isLoading={sending}
                            disabled={!isValid}
                            variant="primary"
                            sx={isXs ? undefined : { height: 56 }}
                          >
                            <Trans>Send reset instructions</Trans>
                          </LoadingButton>
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
                {completeMessage && (
                  <div>
                    <p>{completeMessage}</p>
                    <Button
                      onClick={onClose}
                      color="primary"
                      variant="outlined"
                    >
                      <Trans>Close</Trans>
                    </Button>
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      </PopupContent>
    </Popup>
  );
};

const formSchema = (t) => () => {
  return Yup.object().shape({
    emailAddress: Yup.string()
      .email(t("Invalid email"))
      .required(t("This is required!")),
  });
};

export default ForgotPasswordDialog;
